import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { type Embed as EmbedType } from 'types/capi/content';

import SocialEmbedInteractionEvent, {
  type SocialEmbedPlacement,
  type SocialEmbedProvider
} from '~/events/SocialEmbedInteractionEvent';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';

const MODULE_TYPE = 'embed';

const HTML_URL_ATTRIBUTES = [
  /data-instgrm-permalink="([^"]+)"/,
  /\bcite="([^"]+)"/
];

const HTML_ENTITY = /&(?:#x([0-9a-f]+)|#(\d+)|(amp|apos|gt|lt|quot));/gi;

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  quot: '"'
};

function decodeEntity(
  entity: string,
  hex?: string,
  dec?: string,
  named?: string
) {
  if (named) return NAMED_ENTITIES[named.toLowerCase()] ?? entity;

  try {
    return String.fromCodePoint(parseInt(hex ?? dec ?? '', hex ? 16 : 10));
  } catch {
    return entity;
  }
}

function decodeEntities(value: string) {
  return value.replace(HTML_ENTITY, decodeEntity);
}

function getEmbedUrl({ html, url }: EmbedType) {
  if (url) return url;

  for (const pattern of HTML_URL_ATTRIBUTES) {
    const match = html?.match(pattern);

    if (match?.[1]) return decodeEntities(match[1]);
  }

  return undefined;
}

type ActivationAwareNavigator = Navigator & {
  userActivation?: { hasBeenActive: boolean };
};

function hasUserActivation() {
  const { userActivation } = navigator as ActivationAwareNavigator;

  return userActivation ? userActivation.hasBeenActive : true;
}

function isOutboundLink(target: EventTarget | null) {
  return (
    target instanceof Element && target.closest('a[href^="http"]') !== null
  );
}

export default function useSocialEmbedClick(
  embed: EmbedType,
  provider: SocialEmbedProvider,
  placement: SocialEmbedPlacement,
  modulePosition?: number
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);
  const { isBot } = useRootData();
  const { guestId } = useUser();
  const { key: locationKey } = useLocation();

  const embedUrl = getEmbedUrl(embed);
  const inModule = placement === 'embed_module';
  const isTrackable = Boolean(embedUrl) && Boolean(embed.html) && !isBot;

  const send = useCallback(() => {
    if (!embedUrl) return;

    new SocialEmbedInteractionEvent({
      embedUrl,
      guestId,
      isBot,
      modulePosition: inModule ? modulePosition : undefined,
      moduleType: inModule ? MODULE_TYPE : undefined,
      placement,
      provider
    })
      .send()
      .catch(() => undefined);
  }, [embedUrl, guestId, inModule, isBot, modulePosition, placement, provider]);

  useEffect(() => {
    firedRef.current = false;
  }, [embedUrl, locationKey, provider]);

  useEffect(() => {
    if (!isTrackable) return;

    const container = containerRef.current;

    if (!container) return;

    let pending: ReturnType<typeof setTimeout> | undefined;

    const defer = (check: () => boolean) => {
      clearTimeout(pending);
      pending = setTimeout(check);
    };

    const isInsideEmbed = (node: EventTarget | null) =>
      node instanceof HTMLIFrameElement && container.contains(node);

    const fireFor = (node: EventTarget | null) => {
      if (firedRef.current) return true;
      if (!isInsideEmbed(node)) return false;
      if (!hasUserActivation()) return true;

      firedRef.current = true;
      send();

      return true;
    };

    const rearmUnlessInsideEmbed = () => {
      if (isInsideEmbed(document.activeElement)) return false;

      firedRef.current = false;

      return true;
    };

    const handleBlur = () => {
      if (fireFor(document.activeElement)) return;

      defer(() => fireFor(document.activeElement));
    };

    const handleFocus = () => {
      if (rearmUnlessInsideEmbed()) return;

      defer(rearmUnlessInsideEmbed);
    };

    const handleFrameFocus = (event: FocusEvent) => {
      fireFor(event.target);
    };

    const handleClick = (event: MouseEvent) => {
      if (isOutboundLink(event.target)) send();
    };

    const handleAuxClick = (event: MouseEvent) => {
      if (event.button === 1 && isOutboundLink(event.target)) send();
    };

    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    container.addEventListener('focus', handleFrameFocus, true);
    container.addEventListener('click', handleClick);
    container.addEventListener('auxclick', handleAuxClick);

    return () => {
      clearTimeout(pending);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      container.removeEventListener('focus', handleFrameFocus, true);
      container.removeEventListener('click', handleClick);
      container.removeEventListener('auxclick', handleAuxClick);
    };
  }, [isTrackable, send]);

  return containerRef;
}
